/**
 * Seo component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"
import contentfulToFastlyIo from "@utils/contentful-to-fastly-io"
import { useLocation } from "@reach/router"

const Seo = ({ description, title, socialImage }) => {
  const gatsbyLocation = useLocation()
  const { site, defaultMetaImage } = useStaticQuery(
    graphql`
      query {
        site {
          pathPrefix
          siteMetadata {
            siteUrl
            title
            description
            author
            supportedLocales {
              name
              localePrefix
            }
          }
        }
        defaultMetaImage: contentfulAsset(contentful_id: { eq: "1KOvYgrOJeCUndOpjcDOwJ" }) {
          file {
            url
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const metaImage = socialImage || defaultMetaImage.file.url

  let ogImage, twitterImage

  if (metaImage.includes(".svg")) {
    // Transform w/Contentful Image API (Fastly IO does not support transforming svgs)
    ogImage = `${contentfulToFastlyIo(metaImage)}?fm=png&w=1200&h=630&fit=pad&bg=rgb:ffffff`
    twitterImage = `${contentfulToFastlyIo(metaImage)}?fm=png&w=1200&h=600&fit=pad&bg=rgb:ffffff`
  } else {
    // Transform with Fastly IO
    ogImage = contentfulToFastlyIo(
      metaImage,
      "?auto=avif&canvas=1200:630&width=1200&height=630&fit=bounds&bg-color=FFFFFF"
    )
    twitterImage = contentfulToFastlyIo(
      metaImage,
      "?auto=avif&canvas=1200:600&width=1200&height=600&fit=bounds&bg-color=FFFFFF"
    )
  }

  // default pathname is the path without the locale prefix, i.e. /jp
  const pathname = gatsbyLocation.pathname.replace(/\/$/, "")
  const defaultPathname = pathname.replace(site.pathPrefix, "")

  return (
    <>
      <title>{`${title} | ${site.siteMetadata.title}`}</title>
      {site.siteMetadata.supportedLocales.map((locale, i) => {
        const localePrefix = locale === "en-US" ? "" : locale.localePrefix
        const hrefLang = locale.name === "en-US" ? "en" : locale.name
        return (
          <link
            key={i}
            rel="alternate"
            href={`${site.siteMetadata.siteUrl}${localePrefix}${defaultPathname}`}
            hrefLang={hrefLang}
          />
        )
      })}
      <link rel="alternate" href={`${site.siteMetadata.siteUrl}${defaultPathname}`} hrefLang="x-default" />
      <link rel="canonical" href={`${site.siteMetadata.siteUrl}${pathname}`} />

      <meta name="description" content={metaDescription} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} name="image" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={site.siteMetadata.author} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={twitterImage} />
      {/* TODO: Are keywords used, can this be deleted? */}
      {/* {keywords.length > 0 ? <meta name="keywords" content={keywords.join(", ")} /> : null}*/}
    </>
  )
}

Seo.defaultProps = {
  lang: ``,
  keywords: [],
  description: ``,
}

Seo.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
}

export default Seo
